/**
 * Enregistrement de l'élève PENDANT un exercice (défi streamé, 31/10/2026) : caméra frontale + micro, et la
 * chronologie de l'exercice (question affichée, réponse, note) en ms depuis le début de l'enregistrement. La vidéo
 * reste sur l'appareil tant que l'élève ne la publie pas.
 *
 * Débit bas (≈ 0,5 Mbit/s) et 10 minutes au plus : le fichier tient sous les 50 Mo du bucket `challenges`.
 */
import {
  STREAM_MAX_DURATION_MS,
  STREAM_TIMELINE_MAX_EVENTS,
  type StreamEvent,
} from '@jeraisonne/db';

export const STREAM_VIDEO_BITS_PER_SECOND = 450_000;
export const STREAM_AUDIO_BITS_PER_SECOND = 64_000;

export interface RecordedStream {
  blob: Blob;
  /** type d'envoi (mp4 ou webm) */
  contentType: string;
  ext: string;
  timeline: StreamEvent[];
  durationMs: number;
}

/** Format vidéo : mp4 d'abord (lu partout, iPhone compris), webm sinon ; vide = choix du navigateur. */
export function bestStreamFormat(
  isSupported: (mime: string) => boolean = (m) =>
    typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m),
): { mimeType: string; contentType: string; ext: string } {
  const candidates = [
    { mimeType: 'video/mp4;codecs=avc1,mp4a.40.2', contentType: 'video/mp4', ext: 'mp4' },
    { mimeType: 'video/mp4', contentType: 'video/mp4', ext: 'mp4' },
    { mimeType: 'video/webm;codecs=vp8,opus', contentType: 'video/webm', ext: 'webm' },
    { mimeType: 'video/webm', contentType: 'video/webm', ext: 'webm' },
  ];
  return (
    candidates.find((c) => isSupported(c.mimeType)) ?? { mimeType: '', contentType: '', ext: 'mp4' }
  );
}

/** L'appareil sait-il filmer et enregistrer ? */
export function canRecordExercise(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined'
  );
}

/** Événement de chronologie sans son temps (posé par l'enregistreur). */
export type StreamMark =
  | { kind: 'question'; block_id: string }
  | { kind: 'answer'; block_id: string; choice_ids: string[]; correct: boolean }
  | { kind: 'grade'; correct: number; total: number };

export class ExerciseRecorder {
  readonly stream: MediaStream;
  private readonly recorder: MediaRecorder;
  private readonly chunks: Blob[] = [];
  private readonly events: StreamEvent[] = [];
  private readonly startedAt: number;
  private readonly format: { contentType: string; ext: string };
  private stopped: Promise<RecordedStream> | null = null;
  private limitTimer: ReturnType<typeof setTimeout> | null = null;

  private constructor(stream: MediaStream, now: () => number, onLimit: () => void) {
    this.stream = stream;
    const fmt = bestStreamFormat();
    this.recorder = new MediaRecorder(stream, {
      ...(fmt.mimeType ? { mimeType: fmt.mimeType } : {}),
      videoBitsPerSecond: STREAM_VIDEO_BITS_PER_SECOND,
      audioBitsPerSecond: STREAM_AUDIO_BITS_PER_SECOND,
    });
    this.format = fmt;
    this.now = now;
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.startedAt = now();
    // des morceaux réguliers : rien n'est perdu si l'onglet ralentit
    this.recorder.start(1000);
    this.limitTimer = setTimeout(onLimit, STREAM_MAX_DURATION_MS);
  }

  private readonly now: () => number;

  /** Demande la caméra frontale et le micro, puis démarre. Lève si l'élève refuse. */
  static async start(onLimit: () => void, now: () => number = Date.now): Promise<ExerciseRecorder> {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      audio: true,
    });
    try {
      return new ExerciseRecorder(stream, now, onLimit);
    } catch (e) {
      stream.getTracks().forEach((t) => t.stop());
      throw e;
    }
  }

  /** Temps écoulé depuis le début (ms). */
  elapsed(): number {
    return Math.max(0, this.now() - this.startedAt);
  }

  get recording(): boolean {
    return this.stopped === null;
  }

  /** Pose un événement de la chronologie au temps courant de la vidéo. */
  mark(e: StreamMark): void {
    if (!this.recording || this.events.length >= STREAM_TIMELINE_MAX_EVENTS) return;
    this.events.push({ ...e, t: this.elapsed() } as StreamEvent);
  }

  /** Arrête (une seule fois) : la vidéo et sa chronologie. La caméra s'éteint. */
  stop(): Promise<RecordedStream> {
    if (this.stopped) return this.stopped;
    if (this.limitTimer) clearTimeout(this.limitTimer);
    const durationMs = this.elapsed();
    this.stopped = new Promise<RecordedStream>((resolve) => {
      const finish = () => {
        this.stream.getTracks().forEach((t) => t.stop());
        const type = this.recorder.mimeType || this.chunks[0]?.type || this.format.contentType;
        const contentType = type.startsWith('video/webm') ? 'video/webm' : 'video/mp4';
        resolve({
          blob: new Blob(this.chunks, { type: contentType }),
          contentType,
          ext: contentType === 'video/webm' ? 'webm' : 'mp4',
          timeline: [...this.events],
          durationMs,
        });
      };
      if (this.recorder.state === 'inactive') finish();
      else {
        this.recorder.onstop = finish;
        this.recorder.stop();
      }
    });
    return this.stopped;
  }
}
